import { initializeApp } from 'firebase/app';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  limit,
  Timestamp,
  onSnapshot,
  DocumentData,
  QuerySnapshot
} from 'firebase/firestore';

const firebaseConfig = {
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

export interface PremiumStatus {
  platform: 'Android' | 'iOS' | 'macOS' | 'Web';
  renewalDate: string;
  status: boolean;
  updatedAt: Timestamp | Date;
  userId: string | null;
  uuid: string;
  email?: string;
  name?: string;
  lastEmailSentAt?: string | null;
  lastEmailType?: 'feedback' | 'winback' | null;
}

export interface PremiumStatusMetrics {
  totalPremiumUsers: number;
  activeUsers: number;
  expiredUsers: number;
  platformDistribution: {
    platform: string;
    count: number;
    activeCount: number;
  }[];
  renewalTimeline: {
    date: string;
    count: number;
  }[];
  recentUpdates: PremiumStatus[];
}

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export const fetchPremiumStatusData = async (
  timePeriod: 'day' | 'week' | 'month' = 'month'
): Promise<PremiumStatusMetrics> => {
  try {
    const premiumStatusCollection = collection(db, 'premium_status');
    
    const now = new Date();
    let startDate = new Date();
    
    switch (timePeriod) {
      case 'day':
        startDate.setDate(now.getDate() - 1);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
    }
    
    const q = query(
      premiumStatusCollection,
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const allStatuses: PremiumStatus[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      allStatuses.push({
        platform: data.platform,
        renewalDate: data.renewalDate,
        status: data.status,
        updatedAt: data.updatedAt,
        userId: data.userId,
        uuid: data.uuid || doc.id,
        lastEmailSentAt: data.lastEmailSentAt instanceof Timestamp
          ? data.lastEmailSentAt.toDate().toISOString()
          : (data.lastEmailSentAt ?? null),
        lastEmailType: data.lastEmailType ?? null,
      });
    });
    
    const filteredStatuses = allStatuses.filter(status => {
      if (status.updatedAt instanceof Timestamp) {
        return status.updatedAt.toDate() >= startDate;
      }
      return new Date(status.updatedAt) >= startDate;
    });
    
    const activeUsers = filteredStatuses.filter(s => s.status).length;
    const expiredUsers = filteredStatuses.filter(s => !s.status).length;
    
    const platformMap = new Map<string, { count: number; activeCount: number }>();
    filteredStatuses.forEach(status => {
      const existing = platformMap.get(status.platform) || { count: 0, activeCount: 0 };
      existing.count++;
      if (status.status) {
        existing.activeCount++;
      }
      platformMap.set(status.platform, existing);
    });
    
    const platformDistribution = Array.from(platformMap.entries()).map(([platform, data]) => ({
      platform,
      count: data.count,
      activeCount: data.activeCount
    }));
    
    const renewalMap = new Map<string, number>();
    filteredStatuses.forEach(status => {
      if (status.renewalDate) {
        const renewalDate = new Date(status.renewalDate).toLocaleDateString();
        renewalMap.set(renewalDate, (renewalMap.get(renewalDate) || 0) + 1);
      }
    });
    
    const renewalTimeline = Array.from(renewalMap.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-30);
    
    return {
      totalPremiumUsers: filteredStatuses.length,
      activeUsers,
      expiredUsers,
      platformDistribution,
      renewalTimeline,
      recentUpdates: filteredStatuses
    };
  } catch (error) {
    console.error('Error fetching premium status data:', error);
    throw error;
  }
};

export const subscribeToPremiumStatusUpdates = (
  callback: (metrics: PremiumStatusMetrics) => void,
  timePeriod: 'day' | 'week' | 'month' = 'month'
) => {
  const premiumStatusCollection = collection(db, 'premium_status');
  
  const q = query(
    premiumStatusCollection,
    orderBy('updatedAt', 'desc'),
    limit(100)
  );
  
  return onSnapshot(q, async (snapshot: QuerySnapshot<DocumentData>) => {
    try {
      const metrics = await fetchPremiumStatusData(timePeriod);
      callback(metrics);
    } catch (error) {
      console.error('Error in premium status subscription:', error);
    }
  });
};